import React, {createRef, useState, useContext} from 'react';
import {useQueryClient} from "react-query";
import {useAuth0} from "@auth0/auth0-react";
import axios from "axios";
import moment from "moment";
import __ from "lodash";
import { v4 as uuidv4 } from 'uuid';
import MaterialTable from "@material-table/core";
import { defaultComponents } from '../../../controls/Material-Table-Components';
import {
  Button,
  Checkbox,
  Grid,
  IconButton,
  TablePagination,
  Typography,
  useTheme,
} from "@material-ui/core";
import {
  Email, Message, SubdirectoryArrowRight, Visibility,
} from "@material-ui/icons";
import SystemContext from "../../../SystemContext";
import {getApiSitePrefix} from "../../../functions/SiteDefaults";
import { ColumnWidth } from "../../../blocks/ColumnHelper";
import { getDefaultValues } from "../../../functions/TemplateHelperFunctions"
import {ComboSelect, FlightNumber, HtmlTooltip, LightTooltip, MaskedTime} from '../../../controls';
import AirportLookup from "../../Airports/components/AirportsLookup";
import AirportNameTooltip from "../../Airports/components/AirportNameTooltip";
import CompanyUuidLookup from "../../Companies/components/CompanyUuidLookup";
import SendCateringOrderDialog from "./SendCateringOrderDialog";
import { useCateringOrders, useUpdateCateringOrder, useDeleteCateringOrder } from "../../../services/flights";
import useColour, {getStatusColour} from '../../../services/settings/useColour';

import HistoryDialog from "../../../controls/FormDialog";
import MessageExchangeGrid from "../../../controls/MessageExchangeGrid";
import EditCateringDialog from "../../Trips/components/TripListDialogs/EditCateringDialog";
import CateringOrderVersionsDialog from "./CateringOrderVersionsDialog";


const apiSitePrefix = getApiSitePrefix();
const dateFormat = "DD/MM/YYYY";
const formatChars={
  "U": "[A-Z]",
  "A": "[A-Z0-9]",
  "a": "[A-Za-z0-9]",
  "i": "[A-Za-z0-9\\-]",
  "9": "[0-9]",
  "D": "[0-9.]",
  "-": "-",
  "x": "."
}
const historyButtons = {
  cancel:{icon: "checkCircle", label: "OK", color: "primary"}
};


const CateringOrdersList = (_props) => {
  const props = {
    ..._props
  }
  const {classes} = props;

  const {isAuthenticated} = useAuth0();
  const SystemData = useContext(SystemContext);
  const theme = useTheme();
  const eventBus = SystemData.eventBus;

  const [state] = useState({
    canEdit: props.canEdit,
  });
  const [addOrderDialogStates, setAddOrderDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'New Catering Order'
  });
  const [sendOrderDialogStates, setSendOrderDialogStates] = useState({
    showDialog: false,
    record: {},
    parentRecord: null,
    title: 'Send Catering Order'
  });
  const [defaultValues, setDefaultValues] = useState({
    defaultTemplate: null,
    defaultAttachment: null,
  });
  const [messageExchange, setMessageExchange] = useState({
    showDialog: false,
    record: null,
  });

  const year = props.year;
  const month = props.month;
  const orderVersionsMode = props.orderVersionsMode;
  const parentOrder = props.parentOrder;

  const tableRef = createRef({state: {lastEditingRow: false}});

  const handleUpdateCateringOrder = useUpdateCateringOrder();
  const handleDeleteCateringOrder = useDeleteCateringOrder();

  const queryClient = useQueryClient();
  const extraKeys = orderVersionsMode ? [parentOrder] : [year, month];

  const orders = useCateringOrders({tableRef, queryClient, extraKeys});

  const colours = useColour({code: "CATERING TEXT"});
  const backgroundColours = useColour({code: "CATERING BACKGROUND"});

  const refreshList = async () => {
    queryClient.invalidateQueries(["CateringOrders"]);
  }

  const downloadOrderFile = (orderFile) => {
    window.open(orderFile, "_blank");
  }

  //TODO: do we need to create an order from the list?
  const handleNewOrder = async () => {

    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues();
    }

    const newFuelOrderTemplate = {
      fuelOrderUuid: uuidv4(),
      airportCode: "",
      orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
      lines:[],
      status: "REQUIRED",
      tripNo: null,
      cateringOrderTemplateUuid: defaultValues.defaultTemplate || defaults.defaultTemplate,
      attachmentUuid: defaultValues.defaultAttachment || defaults.defaultAttachment
    }
    setAddOrderDialogStates({...addOrderDialogStates, showDialog: true, record: {...(__.cloneDeep(newFuelOrderTemplate))}, parentRecord: null, title: 'New Fuel Order'});
  }

  const getTemplate = async () => {
    try {
      const response = await axios.get(`${apiSitePrefix}/cateringorders/template`);
      if (!response.data) {
        SystemData.alert("Error", `Could not retrieve Catering Order Template. Server is not responding`);
        return null;
      } else if (response.data.success) {
        return response.data.template;
      } else {
        SystemData.alert("Error", `Could not retrieve Catering Order Template. Server returned ${response.data.error}`);
        return null;
      }
    } catch (err) {
      SystemData.alert("Error", `Could not retrieve Catering Order Template. Server is not responding`);
      return null
    }
  }

  const handleSendOrderClick = async (rowData) => {
    let defaults = {};
    if ( !defaultValues.defaultTemplate || !defaultValues.defaultAttachment) {
      defaults = await getDefaultValues(SystemData, "cateringorders", "CateringOrder");
      setDefaultValues(defaults);
    }

    const flightDate = moment(rowData.flightDate, "YYYY-MM-DD").format("DD-MMM-YYYY")
    const orderSubject = `MAG${rowData.tripNo} // ${rowData.airportCode} Catering Request // ${rowData.airlineCode}${rowData.flightNo} // ${flightDate} // ${rowData.aircraftTailNumber} // ${rowData.departureTime} // Version ${rowData.orderVersion}`;
    if (rowData.orderTemplate === undefined) {
      const template = await getTemplate();
      rowData["orderTemplate"] = template;
    }
    const fileNameIndex = !__.isEmpty(rowData.orderPdfFile) ? rowData.orderPdfFile.lastIndexOf("/") + 1 : 0;
    const fileName = !__.isEmpty(rowData.orderPdfFile) ? rowData.orderPdfFile.substr(fileNameIndex > 0 ? fileNameIndex : 0) : "";
    if (rowData.orderTemplate) {
      const newOrderTemplate = {
        ...rowData,
        orderDate: moment().format("YYYY-MM-DD HH:mm:ss"),
        orderSubject: orderSubject,
        aircraftTailNumber: rowData.aircraftTailNumber ?? "TF-AMP or sub (TF-AMC/AMN/AMI)",
        fileName,
        carbonCopy: defaultValues && __.has(defaultValues, "ccList") ? defaultValues.ccList : defaults.ccList,
      }
      const title = `Send Catering Order${rowData ? " to '" + rowData.companyName + "'":""}${rowData.airportCode ? " at '" + rowData.airportCode + "'": ""}`;
      setSendOrderDialogStates({...sendOrderDialogStates, showDialog: true, record: {...(__.cloneDeep(newOrderTemplate))}, parentRecord: null, title: title});
    }
  }

  const closeSendOrder = () => {
    setSendOrderDialogStates({
      ...addOrderDialogStates,
      showDialog: false,
      record: {},
      parentRecord: null,
      title: 'Send Catering Order'
    });
  }

  const getColumns = (props) => {
    //const validatorColumn = ValidatorColumn(FuelOrdersValidator, true);

    const baseColumn = {
      cellStyle: { fontSize:12, padding: "4px 2px" },
      headerStyle: { padding: "4px 2px" },
      editable: props.canEdit ? "always" : "never",
      emptyValue: "",
      initialEditValue: "",
      sorting: false
    };

    const defaultEditProps = {
      style:{fontSize: "0.875rem"}
    };

    return [
      //{...validatorColumn},
      /*
        Can use ...ColumnWidth(70) to generate a fixed width column
      */

      {
        ...baseColumn,
        ...(ColumnWidth(110, [10,10], undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "At Airport",
        field: "airportCode",
        initialEditValue: "",
        sorting: true,
        editable: "never",
        editComponent: (props) => (
          <AirportLookup
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: (rowData) => (
          <LightTooltip title={<AirportNameTooltip airportCode={rowData.airportCode} />}>
            <div>
              {rowData.status === "SECTOR" ? <div><SubdirectoryArrowRight/><span> {rowData.airportCode} </span></div> : <span> {rowData.airportCode} </span>}
            </div>
          </LightTooltip>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Status",
        field: "status",
        editable: state.canEdit ? "always" : "never",
        emptyValue: "",
        initialEditValue: "REQUIRED",
        fullWidth: true,
        sorting: true,
        cellStyle: (value, rowData) => {
          const backgroundColor = getStatusColour(backgroundColours, value);
          const color = getStatusColour(colours, value);
          const {cellStyle} = ColumnWidth(100);
          return ({...cellStyle, color, backgroundColor, textAlign: "center", border: `1px solid #d0d0d0`, fontWeight: 600});
        },
        render: rowData => (
          <ComboSelect
            group="CATERING ORDER STATUS"
            subGroup=""
            value={rowData.status}
            displayOnly={true}
          />
        ),
        editComponent: (props) => {
          return (
            <ComboSelect
              group="CATERING ORDER STATUS SELECT"
              subGroup=""
              fullWidth
              value={props.value}
              onChange={props.onChange}
            />
          )
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, {paddingLeft: 12}, {paddingLeft: 12})),
        title: "Ordered On",
        field: "orderDate",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.orderDate).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.orderDate ? moment.utc(rowData.orderDate).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...ColumnWidth(400,2,undefined,undefined,undefined,undefined,false),
        title: "From",
        field: "companyName",
        editable: "never",
        editComponent: (props) => (
          <CompanyUuidLookup
            companyType={"SUPPLIER"}
            value={props.value}
            returnType={"object"}
            onChange={(returnObject) => {
              if (returnObject.found) {
                const returnedData = returnObject.data
                const data = {
                  ...props.rowData,
                  supplierName: returnedData.companyName,
                  supplierUuid: returnedData.companyUuid,
                }
                props.onRowDataChange(data);
              } else {
                props.onChange(returnObject.value);
              }
            }}
          />
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,2,undefined,undefined,undefined,undefined,false),
        title: "Version",
        field: "orderVersion",
        editable: "never",
        render: rowData => (
          <Button
            onClick={(event) => {
              if (rowData.orderVersion > 1 && rowData.parentOrderUuid === null)
                eventBus.trigger("CateringOrderVersionsDialog.open", rowData)
            }}
            color={rowData.orderVersion > 1 && rowData.parentOrderUuid === null ? "primary" : undefined}
            endIcon={rowData.orderVersion > 1 && rowData.parentOrderUuid === null ? <Visibility/> : <></>}
          >
            {rowData.orderVersion}

          </Button>
        )
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,2,undefined,undefined,undefined,undefined,false),
        title: "Flight Number",
        field: "flightNo",
        editable: "never",
        editComponent: (props) => (
          <FlightNumber
            mask={"XX-999aa"}
            code={props.rowData.airlineCode}
            number={props.rowData.flightNo}
            inputProps={defaultEditProps}
            onChange={e => {
              const airlineCode = e.code;
              const flightNo = parseInt(e.number);
              let data = { ...props.rowData, airlineCode, flightNo };
              props.onRowDataChange(data);
            }}
          />
        ),
        render: (props) => (
          <div style={{ width: 162 }}>
            {props.airlineCode}&nbsp;{props.flightNo}
          </div>
        ),
      },
      {
        ...baseColumn,
        ...(ColumnWidth(130, undefined, undefined, undefined, undefined)),
        title: "Flight Date",
        field: "flightDate",
        editable: "never",
        customFilterAndSearch: (term, rowData) => {
          const searchDate = moment.utc(rowData.flightDate).startOf('day').format('DD MMM YYYY').toUpperCase();
          const termUpper = `${term}`.toUpperCase();
          return searchDate.indexOf(termUpper) !== -1
        },
        render: rowData => {
          const displayDate = rowData.flightDate ? moment.utc(rowData.flightDate).format(dateFormat) : "Not Sent Yet";
          return (<div>{displayDate}</div>);
        },
      },
      {
        ...baseColumn,
        ...(ColumnWidth(70, undefined, undefined, undefined, undefined)),
        title: "Departure Time",
        field: "departureTime",
        editable: "never",
        editComponent: (props) => (
          <MaskedTime
            value={props.value}
            onChange={props.onChange}
          />
        ),
        render: rowData => (
          <div>{rowData.departureTime?.substr(0,Math.min(rowData.departureTime?.length, 5))}</div>
        ),
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,2,undefined,undefined,undefined,undefined,false),
        title: "Aircraft",
        field: "aircraftTailNumber",
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(70,2,undefined,undefined,undefined,undefined,false),
        title: "Crew Number",
        field: "crewNumber",
        editable: "never",
      },
      {
        ...baseColumn,
        ...ColumnWidth(120,2,undefined,undefined,undefined,undefined,false),
        title: "Replenish Dry Stores",
        field: "replenishDryStore",
        type: "boolean",
        editable: "never",
        emptyValue: false,
        initialEditValue: false,
        render: rowData => <Checkbox style={{padding: 0}} checked={rowData.replenishDryStore === true} value="true" />
      },
      {
        ...baseColumn,
        //...ColumnWidth(120,2,undefined,undefined,undefined,undefined,false),
        title: "Additional Sectors",
        field: "additionalSectors",
        editable: "never",
        render: rowData => {
          const sectors = [];
          if (rowData.additionalSectors && Array.isArray(rowData.additionalSectors) && rowData.additionalSectors.length > 0) {
            rowData.additionalSectors.forEach(sector => {
              sectors.push(`${sector.originAirportCode}-${sector.destinationAirportCode}`);
            });
          }
          return <span>{sectors.join(", ")}</span>
        }
      },
      {
        ...baseColumn,
        ...(ColumnWidth(10,)),
      },
    ];
  };

  return (isAuthenticated &&
    <div style={{width: "100%", marginLeft: orderVersionsMode ? 0 : 20, marginRight: orderVersionsMode ? 0: 20}}>
      <MaterialTable
        //icons={{ DetailPanel: React.forwardRef((props, ref) => <Icon>unfold_more</Icon> )}}
        tableRef={tableRef}
        style={{
          border: orderVersionsMode ? '2px solid #d3d4d5' : "none",
          boxShadow: "none",
          borderRadius: orderVersionsMode ? 1 : 0,
        }}
        options={{
          addRowPosition: "first",
          actionsColumnIndex: -1,
          cellStyle: {
            padding: "8px 2px",
          },
          headerStyle: {
            backgroundColor: orderVersionsMode ? '#eaeaea' : '#3f3f3f',
            color: orderVersionsMode ? '#1e1e1e' : theme.palette.primary.main,
            padding: "2px",
          },
          toolbarButtonAlignment: "right",
          pageSize: orderVersionsMode ? 5 : 15,
          detailPanelType: "single",
          padding: "dense",
          // overflowY: "scroll",
          search: orderVersionsMode ? false : true,
          toolbar: orderVersionsMode ? false : true,
        }}
        components={{
          ...defaultComponents,
          Pagination: (props) => (
            <TablePagination
              {...props}
              rowsPerPageOptions={[5, 10, 15]}
            />
          ),
        }}
        actions={[
          {
            icon: 'refresh',
            tooltip: "Refresh List",
            isFreeAction: true,
            onClick: (e) => {
              refreshList();
            }
          },
          // {
          //   hidden: false,
          //   disabled: false,
          //   icon: () => (
          //     <IconButton>
          //       <AddBox/>
          //     </IconButton>
          //   ),
          //   iconProps: { style: { color: '#fc7c35' }},
          //   tooltip: "New Catering Order",
          //   isFreeAction: true,
          //   onClick: handleNewOrder
          // },
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit || ["SECTOR", "CANCELLED"].includes(rowData.status),
              icon: () => (
                <IconButton variant="contained" size={"small"} >
                  <Email style={(!state.canEdit || ["SECTOR", "CANCELLED"].includes(rowData.status)) ? {color: '#b3b0b0'} : { color: theme.palette.primary.main}}/>
                </IconButton>
              ),
              tooltip: "Email Order" +  (!state.canEdit ? " - only available with Edit privileges" : ["SECTOR", "CANCELLED"].includes(rowData.status) ? " - Not available for this order status" : ""),
              iconProps: { style: { color: theme.palette.primary.main }},
              isFreeAction: false,
              onClick: (e, rowData) => {
                handleSendOrderClick(rowData);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: !state.canEdit || !rowData.orderPdfFile,
              icon: 'get_app',
              iconProps: { style: (state.canEdit && rowData.orderPdfFile) ? { color: theme.palette.primary.main, marginTop: 5 } : {color: '#b3b0b0', marginTop: 5}},
              tooltip: "Download Order File" + (!rowData.orderPdfFile ? " - File wasn't uploaded yet" : ""),
              isFreeAction: false,
              onClick: (e, rowData) => {
                downloadOrderFile(rowData.orderPdfFile);
              }
            }
          ),
          (rowData) => (
            {
              hidden: false,
              disabled: false,
              icon: () => (
                <HtmlTooltip
                  rowData={rowData}
                  title={
                    <React.Fragment>
                      <Typography color="inherit">Message Exchange</Typography>
                      <div style={{ whiteSpace: "pre-wrap" }}>
                        {
                          rowData && __.has(rowData, "messageExchange") && rowData.messageExchange !== null
                            ?
                            rowData.messageExchange.map(exchange => {
                              const date = moment(exchange.messageDate, "YYYY=MM-DD").format("DD/MM/YYYY");
                              const display = `${date} - ${exchange.action}`;
                              return <div><span>{display}</span><br/></div>
                            })
                            : <></>
                        }
                      </div>
                    </React.Fragment>
                  }
                >
                  <IconButton
                    variant="contained" color="primary" size={"small"}
                  >
                    <Message style={(!rowData.messageExchange || !Array.isArray(rowData.messageExchange) || rowData.messageExchange.length <= 0) ? {color: '#b3b0b0'} : { color: theme.palette.primary.main}}/>
                  </IconButton>
                </HtmlTooltip>),
              iconProps: { style: { color: theme.palette.primary.main } },
              isFreeAction: false,
              onClick: (e, rowData) => {
                if (rowData.messageExchange && Array.isArray(rowData.messageExchange) && rowData.messageExchange.length > 0)
                  setMessageExchange({showDialog: true, rowData: rowData});
              }
            }
          ),
        ]}
        title="Catering Orders"
        columns={getColumns({canEdit: state.canEdit})}
        data={orders.data}
        parentChildData={(row, rows) => rows.find(a => a.cateringOrderUuid === row.parentOrderUuid)}
        editable={{
          isEditable: (rowData) => {
            return state.canEdit && rowData.parentOrderUuid === null;
          },
          isDeletable: (rowData) => {
            return state.canEdit && rowData.parentOrderUuid !== null && !orderVersionsMode;
          },
          onRowAdd: undefined,
          onRowUpdate: (newData, oldData) =>
            new Promise((resolve, reject) => {
              handleUpdateCateringOrder.mutate({record: newData, oldRecord: oldData, resolve, reject, extraKeys});
            }),
          onRowDelete: (oldData) =>
            new Promise((resolve, reject) => {
              handleDeleteCateringOrder.mutate({oldRecord: oldData, resolve, reject, extraKeys});
            }),
        }}
      />

      {sendOrderDialogStates.showDialog
        ?
          <SendCateringOrderDialog
            dialogStates={sendOrderDialogStates}
            SystemData={SystemData}
            extraKeys={extraKeys}
            formatChars={formatChars}
            classes={classes}
            close={()=>closeSendOrder()}
          />
        :
          <></>
      }
      {messageExchange.showDialog
        ?
        <HistoryDialog
          title="Order Message Exchange"
          message=""
          open={messageExchange.showDialog}
          record={{notes: messageExchange.rowData.messageExchange}}
          dialogOptions={{maxWidth: "md", fullWidth: true}}
          buttons={historyButtons}
          onChange={(result) => {
            setMessageExchange({...messageExchange, showDialog: false, rowData: null});
          }}
        >
          <Grid container checkChildren={true} spacing={3}>
            <Grid item xs={12} checkChildren={true}>
              <MessageExchangeGrid messages={messageExchange.rowData.messageExchange}/>
            </Grid>
          </Grid>
        </HistoryDialog>
        :
        <></>
      }

      <EditCateringDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        handleUpdateFlight={()=> {}}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
      />

      <CateringOrderVersionsDialog
        systemData={SystemData}
        apiSitePrefix={apiSitePrefix}
        extraKeys={extraKeys}
        formatChars={formatChars}
        classes={classes}
        canEdit={state.canEdit}
      />

    </div>
  );

}

export default CateringOrdersList;
